import { Plus, Trash2, Users } from 'lucide-react';
import { ContractPlanInput } from '@/services/contractApi';
import { PlanTier } from '@/services/organizationApi';

interface Props {
  plans: ContractPlanInput[];
  setPlans: (plans: ContractPlanInput[]) => void;
  availablePlans: PlanTier[];
}

/**
 * Tableau éditable de plans pour la CRÉATION d'un contrat multi-plan.
 *
 * Différences vs ContractPlansEditor (édition) :
 *   - Composant contrôlé (state remonté au parent) — la soumission est
 *     orchestrée par ContractPage avec les autres champs du contrat.
 *   - Pas de modale downgrade : à la création, aucun user n'est encore
 *     affecté à un plan, donc le check d'overflow se fait sur la somme
 *     totale côté backend (QUOTA_BELOW_ACTIVE_USERS).
 *   - Pas de notion de "isExisting" : toutes les lignes sont nouvelles.
 */
export function CreationPlansEditor({ plans, setPlans, availablePlans }: Props) {
  const usedSlugs = new Set(plans.map((p) => p.planSlug).filter(Boolean));
  const addablePlans = availablePlans.filter(
    (p) => p.slug !== 'solo' && !usedSlugs.has(p.slug),
  );

  const totalSeats = plans.reduce((sum, p) => sum + (p.teamQuantity || 0), 0);

  // Retourne le min_seats du plan correspondant au slug, fallback 1.
  function minSeatsFor(slug: string): number {
    return availablePlans.find((p) => p.slug === slug)?.min_seats ?? 1;
  }

  function addRow() {
    if (addablePlans.length === 0) return;
    // Ordre de préférence : lead, solve, detect, beta. Fallback : premier addable.
    const preferenceOrder = ['lead', 'solve', 'detect', 'beta'];
    const preferred =
      preferenceOrder
        .map((slug) => addablePlans.find((p) => p.slug === slug))
        .find((p) => p !== undefined)
      ?? addablePlans[0];
    setPlans([...plans, { planSlug: preferred.slug, teamQuantity: preferred.min_seats ?? 5 }]);
  }

  function removeRow(idx: number) {
    setPlans(plans.filter((_, i) => i !== idx));
  }

  function updateRow(idx: number, patch: Partial<ContractPlanInput>) {
    setPlans(plans.map((p, i) => {
      if (i !== idx) return p;
      const next = { ...p, ...patch };
      // Si on change le slug, on clamp la quantité au min du nouveau plan
      // pour éviter de rester sous min_seats après un switch lead → solve.
      if (patch.planSlug && patch.planSlug !== p.planSlug) {
        const min = minSeatsFor(patch.planSlug);
        if (next.teamQuantity < min) next.teamQuantity = min;
      }
      return next;
    }));
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-900">
          Plans du contrat
        </h2>
        <span className="text-xs text-gray-500">
          <Users className="w-3 h-3 inline mr-1" />
          {totalSeats} siège{totalSeats > 1 ? 's' : ''} au total
        </span>
      </div>

      <p className="text-xs text-gray-500 mb-3">
        Composez le mix de plans achetés (ex : 5 Lead + 30 Solve + 25 Detect). Le premier plan
        de la liste sera utilisé comme plan principal pour la rétro-compatibilité.
      </p>

      <div className="space-y-2 mb-3">
        {plans.length === 0 && (
          <p className="text-sm text-gray-500 py-3 italic">Aucun plan — ajoutez-en au moins un.</p>
        )}
        {plans.map((row, idx) => {
          const rowMin = minSeatsFor(row.planSlug);
          const belowMin = row.teamQuantity < rowMin;
          return (
          <div
            key={`${row.planSlug}-${idx}`}
            className={`flex items-center gap-3 p-3 rounded-lg border ${
              belowMin ? 'border-rose-300 bg-rose-50' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <select
              value={row.planSlug}
              onChange={(e) => updateRow(idx, { planSlug: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              {availablePlans
                .filter((p) => p.slug !== 'solo' && (p.slug === row.planSlug || !usedSlugs.has(p.slug)))
                .map((p) => (
                  <option key={p.id} value={p.slug}>{p.name}</option>
                ))}
            </select>
            <input
              type="number"
              min={rowMin}
              value={row.teamQuantity}
              onChange={(e) => updateRow(idx, { teamQuantity: parseInt(e.target.value, 10) || rowMin })}
              className={`w-24 px-3 py-2 border rounded-lg text-sm ${
                belowMin ? 'border-rose-400 bg-white' : 'border-gray-300'
              }`}
              aria-label="Sièges"
              aria-invalid={belowMin}
              title={`Minimum ${rowMin} siège${rowMin > 1 ? 's' : ''} pour ce plan`}
            />
            <span className="text-xs text-gray-400 w-20 text-right">
              {belowMin ? `min ${rowMin}` : 'sièges'}
            </span>
            <button
              onClick={() => removeRow(idx)}
              className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg"
              title="Retirer ce plan du mix"
              type="button"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          );
        })}
      </div>

      <button
        onClick={addRow}
        disabled={addablePlans.length === 0}
        className="flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:text-gray-400 disabled:hover:bg-transparent"
        type="button"
      >
        <Plus className="w-4 h-4" />
        Ajouter un plan
      </button>
    </div>
  );
}
